import { gsap } from 'gsap/all'
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin'
import { MotionPathPlugin } from 'gsap/MotionPathPlugin'
import { events, math, store } from '../../src/js/core/store.js'
import { SplitTextInstance } from '../../src/js/parts/split-text.js'
import { Reveal } from '../../src/js/parts/reveal.js'
import { Canvas } from './canvas.js'
import { Modal } from './modal.js'
import { Video } from './video.js'

gsap.registerPlugin(DrawSVGPlugin, MotionPathPlugin)

export let globeInstance

export const setGlobalInstance = mode => {
  globeInstance = mode
}

export const initGlobeHeader = () => {
  const globe = document.querySelector('.js-globe')
  if (!globe) { return false }

  globeInstance = new Globe(globe)
}

class Globe {
  constructor (globe) {
    this.globe = globe
    this.svg = this.globe.querySelector('.js-svg')
    this.bounds = this.svg.getBoundingClientRect()
    this.layer = document.querySelector('.js-globe-layer')
    this.path = this.globe.querySelector('#js-path')
    this.knob = this.globe.querySelector('#js-drag')
    this.isTriggered = false
    this.progress = 0
    this.previewProgress = 0
    this.speed = 0.2
    this.isPulsing = false
    this.hasHovered = false
    this.init()
  }

  createTimeline () {
    this.timeline = gsap.timeline({ paused: true })

    this.timeline.to(this.knob, 1, {
      motionPath: {
        autoRotate: true,
        align: this.path,
        path: this.path,
        alignOrigin: [0.5, 0.5]
      },
      ease: 'none'
    }, 0)

    this.timeline.progress(0.01)
  }

  render = () => {
    if (this.canvas.inView && !this.video.isOpen && this.isTriggered) {
      this.canvas.render()
    } else if (this.canvas.inView && !this.hasHovered) {
      this.isPulsing = true
    }

    this.transform()
    window.requestAnimationFrame(this.render)
  }

  destroy = () => {
    window.cancelAnimationFrame(this.render)
  }

  transform () {
    this.previewProgress = math.lerp(this.previewProgress, this.progress, this.speed)
    this.timeline.progress(this.previewProgress)
  }

  createDraggable () {
    this.knob.addEventListener(events.hoverStart, () => {
      this.hasHovered = true
      this.isPulsing = false
      this.pulseTimeline.pause()

      gsap.to(this.knob.querySelector('path'), 0.6, {
        fill: '#052827',
        ease: 'expo.out'
      })

      gsap.to(this.knob.querySelector('ellipse'), 0.6, {
        fill: 'white',
        ease: 'expo.out'
      })

      gsap.to(this.knob, 0.6, {
        scale: 1.2,
        ease: 'expo.out'
      })
    })

    this.knob.addEventListener(events.hoverEnd, () => {
      if (this.isDragging) { return false }

      gsap.to(this.knob, 0.6, {
        scale: 1,
        ease: 'expo.out'
      })
    })

    this.knob.addEventListener(events.dragStart, () => {
      if (this.isTriggered) { return false }

      document.body.style.overflow = 'hidden'
      this.speed = 0.2
      this.isDragging = true
    })

    window.addEventListener(events.drag, e => {
      if (this.isTriggered) { return false }

      if (this.isDragging) {
        this.update(e)
      }
    })

    window.addEventListener(events.dragEnd, () => {
      if (this.isTriggered) { return false }
      document.body.style.overflow = 'unset'

      gsap.to(this.knob, 0.6, {
        scale: 1,
        ease: 'expo.out'
      })

      this.isDragging = false
      this.progress = 0
      this.speed = 0.0375
    })

    window.addEventListener('resize', () => {
      this.bounds = this.svg.getBoundingClientRect()
    })
  }

  update = e => {
    if (!this.isDragging) { return false }

    const x = store.isDevice ? e.touches[0].clientX : e.clientX
    this.progress = Math.min(Math.max((x - this.bounds.left) / this.bounds.width, 0), 1)

    if (this.progress >= 0.5) {
      this.isTriggered = true

      gsap.to(this, 0.1, {
        progress: 1,
        ease: 'power4.out',
        onComplete: () => {
          document.body.style.overflow = 'unset'

          setTimeout(() => {
            this.hideInitialContent()
            this.canvas.rotate()
            this.canvas.inView = true
            this.tweenYears()
          }, 750)
        }
      })
    }
  }

  hideInitialContent () {
    const elements = [...document.querySelectorAll('.js-globe h1 .js-in-view div div')]
    const ups = [...document.querySelectorAll('.js-globe [data-name="up"]')]

    gsap.to(elements, 0.75, {
      y: '-140%',
      ease: 'expo.out',
      stagger: 0.05
    })

    gsap.to(ups, 0.75, {
      stagger: 0.15,
      y: '-100%',
      opacity: 0,
      ease: 'power4.out'
    })

    document.querySelector('.globe_header__frame').style.pointerEvents = 'none'
    document.querySelector('canvas').style.cursor = 'grab'
  }

  tweenYears () {
    const list = document.querySelector('.js-list')
    const last = document.querySelector('.js-last')
    const numbers = [...document.querySelectorAll('.js-number')]
    const decade = document.querySelector('.js-decade')
    const thisIs = document.querySelector('.js-this-is')
    const lastText = document.querySelector('.js-last-text')

    gsap.timeline({
      ease: 'expo.inOut',
      delay: 0.1,
      onComplete: () => {
        gsap.to(thisIs, 0.3, {
          opacity: 1,
          y: '-50%',
          ease: 'power4.out'
        })

        new SplitTextInstance(lastText, 'words')
        new Reveal(lastText, 'words').reveal()
      }
    })
      .to(decade, { y: '-50%', opacity: 1, duration: 0.3 }, 0.3)
      .to(list, { y: '0%', duration: 0.6 }, 0.3)
      .to(numbers, { y: '-900%', duration: 3 }, 0.9)
      .to(list, { y: '-100%', duration: 0.3 }, 3.9)
      .to(last, { y: '-100%', duration: 0.3 }, 3.9)
      .to(decade, { y: '-100%', opacity: 0, duration: 0.3 }, 3.9)
  }

  pulse () {
    this.pulseTimeline = gsap.timeline({
      ease: 'expo.out',
      repeat: -1,
      delay: 2,
      repeatDelay: 2,
      onComplete: () => {
        if (this.isPulsing) {
          this.pulseTimeline.restart()
        }
      }
    })
      .to(this.knob, 0.6, {
        scale: 1.2
      }, 0)
      .to(this.knob.querySelector('path'), 0.6, {
        fill: 'white'
      }, 0)
      .to(this.knob.querySelector('ellipse'), 0.6, {
        fill: '#f70'
      }, 0)
      .to(this.knob, 0.6, {
        scale: 1
      }, 1)
      .to(this.knob.querySelector('path'), 0.6, {
        fill: '#052827'
      }, 1)
      .to(this.knob.querySelector('ellipse'), 0.6, {
        fill: 'white'
      }, 1)
  }

  init () {
    this.canvas = new Canvas()
    this.modal = new Modal()
    this.video = new Video()
    this.createTimeline()
    this.createDraggable()
    this.pulse()
    this.render()
  }
}

